import { useState } from "react";
import { useDebouncedValue } from "@exonn/hooks";
import type { SupplierAgreementResource } from "@exonn/js-sdk";
import { Select } from "src/components/Select/Select.tsx";
import { useInfiniteSelect } from "src/system/useInfiniteSelect.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import type { SupplierOrderResource } from "@exonn/js-sdk";

type Props = {
  order: SupplierOrderResource;
  label: string;
  value: string;
  onChange: (value: string) => void;
};

const label = (agreement: SupplierAgreementResource) =>
  [agreement.supplier_sku, agreement.variant?.sku, agreement.variant?.title]
    .filter(Boolean)
    .join(" · ");

// Артикулы, которые можно заказать: соглашения поставщика заказа для его манданта и типа закупки.
export const AgreementSelect = ({
  order,
  label: fieldLabel,
  value,
  onChange,
}: Props) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);

  const {
    options,
    query: { isFetchingNextPage, fetchNextPage, isLoading, hasNextPage },
  } = useInfiniteSelect<SupplierAgreementResource>({
    resource: ResourceEnum.SupplierAgreement,
    search: debouncedSearch,
    searchField: "supplier_sku",
    labelProp: label,
    include: ["variant"],
    pageSize: 20,
    filter: {
      items: [
        {
          field: "supplier_id",
          operator: "is",
          value: order.supplier_id ?? "",
        },
        { field: "owner_id", operator: "is", value: order.owner_id ?? "" },
        {
          field: "procurement_type",
          operator: "is",
          value: order.procurement_type,
        },
      ],
    },
  });

  return (
    <Select
      showSearch
      label={fieldLabel}
      searchValue={search}
      onSearchValueChange={setSearch}
      isLoadingData={isLoading}
      isLoadingMoreOptions={isFetchingNextPage}
      onScrollEnd={() => {
        if (!isFetchingNextPage && hasNextPage) {
          fetchNextPage();
        }
      }}
      value={value}
      onChange={(next) => onChange(next ?? "")}
      options={options}
    />
  );
};
